import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { getLscAdminLeagueSummary } from '../api';
import type { LscAdminLeagueSummary, LscSupportIssueCategory, LscSupportIssueSeverity } from '../types';
import LoadingState from '../components/LoadingState';
import { lscPath } from '../routing';
import { displaySeasonLabel } from '../seasonDisplay';
import {
  adminIssueStatusLabel,
  ADMIN_FORBIDDEN_CONTROL_LABELS,
  compactLeagueId,
} from '../adminIssueCopy';
import {
  adminLeagueSummaryIdentityLine,
  formatAdminSummaryValue,
  LSC_ADMIN_LEAGUE_SUMMARY_ARCHIVE_NOTE,
  LSC_ADMIN_LEAGUE_SUMMARY_INTRO,
  LSC_ADMIN_LEAGUE_SUMMARY_ISSUES_EMPTY,
  LSC_ADMIN_LEAGUE_SUMMARY_NAVIGATION_NOTE,
  LSC_ADMIN_LEAGUE_SUMMARY_TITLE,
  parseAdminLeagueSummaryError,
} from '../adminLeagueSummaryCopy';
import { supportIssueCategoryLabel, supportIssueSeverityLabel } from '../supportIssueCopy';

function SummaryCard({
  title,
  testId,
  children,
}: {
  title: string;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <section className="card space-y-3 text-sm" data-testid={testId}>
      <h3 className="font-semibold text-white">{title}</h3>
      {children}
    </section>
  );
}

function SummaryField({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="flex justify-between gap-4 text-secondary-400">
      <dt>{label}</dt>
      <dd className="text-right text-secondary-200 break-all">{formatAdminSummaryValue(value)}</dd>
    </div>
  );
}

function ReadinessItemList({
  items,
  emptyLabel,
}: {
  items: Array<{ key?: string; title?: string; status?: string; description?: string }>;
  emptyLabel: string;
}) {
  if (!items.length) {
    return <p className="text-secondary-500">{emptyLabel}</p>;
  }
  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item.key || item.title} className="rounded border border-secondary-800 p-2">
          <p className="text-white">{item.title || item.key}</p>
          <p className="text-secondary-500 text-xs">{item.status || 'unknown'}</p>
          {item.description ? <p className="text-secondary-400 mt-1">{item.description}</p> : null}
        </li>
      ))}
    </ul>
  );
}

export function LscAdminLeagueSummaryPageView({
  summary,
  loading,
  error,
}: {
  summary: LscAdminLeagueSummary | null;
  loading: boolean;
  error: string | null;
}) {
  if (loading) return <LoadingState message="Loading league health summary..." />;

  if (error) {
    return (
      <div className="space-y-3" data-testid="lsc-admin-league-summary-error">
        <p className="text-sm text-red-300" role="alert">
          {error}
        </p>
        <Link to={lscPath('/admin/issues')} className="text-primary-300 hover:text-primary-200 text-sm">
          Back to issue queue
        </Link>
      </div>
    );
  }

  if (!summary) return null;

  const identity = summary.identity;
  const latestIssues = summary.issues.latest_issues ?? [];

  return (
    <div className="space-y-6" data-testid="lsc-admin-league-summary-page">
      <div className="space-y-2">
        <Link
          to={lscPath('/admin/issues')}
          className="text-sm text-primary-300 hover:text-primary-200"
          data-testid="lsc-admin-league-summary-back"
        >
          ← Back to issue queue
        </Link>
        <h2 className="text-xl font-semibold text-white">{LSC_ADMIN_LEAGUE_SUMMARY_TITLE}</h2>
        <p className="text-sm text-secondary-400">{LSC_ADMIN_LEAGUE_SUMMARY_INTRO}</p>
        <p className="text-lg text-white">{adminLeagueSummaryIdentityLine(identity)}</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <SummaryCard title="League identity" testId="lsc-admin-summary-identity-card">
          <dl className="grid gap-2">
            <SummaryField label="League ID" value={compactLeagueId(identity.league_id)} />
            <SummaryField label="Name" value={identity.league_name} />
            <SummaryField label="Slug" value={identity.slug} />
            <SummaryField label="Game" value={identity.game_key} />
            <SummaryField label="Platform" value={identity.platform} />
            <SummaryField label="Owner UID" value={identity.owner_uid_compact} />
            <SummaryField label="Created" value={identity.created_at} />
            <SummaryField label="Status" value={identity.status} />
          </dl>
          <Link
            to={lscPath(`/leagues/${identity.league_id}/command-center`)}
            className="inline-flex text-primary-300 hover:text-primary-200"
            data-testid="lsc-admin-summary-command-center-link"
          >
            Open league command center
          </Link>
        </SummaryCard>

        <SummaryCard title="Billing and entitlement" testId="lsc-admin-summary-billing-card">
          <dl className="grid gap-2">
            <SummaryField label="Billing state" value={summary.billing.billing_state} />
            <SummaryField label="Subscription status" value={summary.billing.subscription_status} />
            <SummaryField label="Plan" value={summary.billing.plan_display_name || summary.billing.plan_key} />
            <SummaryField label="Cadence" value={summary.billing.billing_cadence} />
            <SummaryField label="Entitlement bound" value={summary.billing.bound} />
            <SummaryField label="Comped" value={summary.billing.is_comped} />
            <SummaryField label="Read-only" value={summary.billing.read_only} />
          </dl>
        </SummaryCard>

        <SummaryCard title="Readiness blockers" testId="lsc-admin-summary-readiness-card">
          <dl className="grid gap-2 mb-3">
            <SummaryField label="Overall status" value={summary.readiness.overall_status} />
            <SummaryField label="Setup status" value={summary.readiness.setup_status} />
            <SummaryField label="Complete" value={summary.readiness.is_complete} />
          </dl>
          <p className="text-xs uppercase tracking-wide text-secondary-500">Blocking items</p>
          <ReadinessItemList
            items={summary.readiness.blocking_items ?? []}
            emptyLabel="No blocking readiness items."
          />
          <p className="text-xs uppercase tracking-wide text-secondary-500 mt-4">Warnings</p>
          <ReadinessItemList
            items={summary.readiness.warning_items ?? []}
            emptyLabel="No readiness warnings."
          />
        </SummaryCard>

        <SummaryCard title="Setup navigation" testId="lsc-admin-summary-setup-card">
          <p className="text-xs text-secondary-500">{LSC_ADMIN_LEAGUE_SUMMARY_NAVIGATION_NOTE}</p>
          <dl className="grid gap-2">
            <SummaryField label="Setup status" value={summary.setup_navigation.setup_status} />
            <SummaryField label="Current step" value={summary.setup_navigation.current_step_id} />
            <SummaryField label="Resume step" value={summary.setup_navigation.resume_step_id} />
            <SummaryField label="Activation locked" value={summary.setup_navigation.activation_locked} />
          </dl>
        </SummaryCard>

        <SummaryCard title="Automation and runtime" testId="lsc-admin-summary-automation-card">
          <dl className="grid gap-2 mb-3">
            <SummaryField label="Overall status" value={summary.automation.overall_status} />
          </dl>
          <dl className="grid gap-2">
            {Object.entries(summary.automation.lanes ?? {}).map(([laneKey, lane]) => (
              <SummaryField
                key={laneKey}
                label={lane.scope_label || laneKey}
                value={[lane.status, lane.effective_state].filter(Boolean).join(' · ') || '—'}
              />
            ))}
          </dl>
        </SummaryCard>

        <SummaryCard title="Schedule health" testId="lsc-admin-summary-schedule-card">
          <dl className="grid gap-2">
            <SummaryField
              label="Active season"
              value={displaySeasonLabel(summary.schedule.active_season_label)}
            />
            <SummaryField label="Active week/stage" value={summary.schedule.active_stage_label} />
            <SummaryField label="Season phase" value={summary.schedule.season_phase} />
            <SummaryField label="Schedule source connected" value={summary.schedule.schedule_source_connected} />
            <SummaryField label="Source state stale" value={summary.schedule.source_state_stale} />
            <SummaryField label="Schedule stale" value={summary.schedule.is_stale} />
            <SummaryField label="Repair needed" value={summary.schedule.repair_needed} />
            <SummaryField label="Recommended action" value={summary.schedule.recommended_action} />
          </dl>
        </SummaryCard>

        <SummaryCard title="Live, VOD, and archive" testId="lsc-admin-summary-workflows-card">
          <dl className="grid gap-2">
            <SummaryField label="Needs Attention count" value={summary.workflows.needs_attention_count} />
            <SummaryField
              label="Side confirmation blockers"
              value={summary.workflows.side_confirmation_blockers_count}
            />
            <SummaryField label="VOD handoff blockers" value={summary.workflows.vod_handoff_blockers_count} />
            <SummaryField label="Archive review count" value={summary.workflows.archive_review_count} />
            <SummaryField
              label="Archive autopublish mode"
              value={summary.workflows.archive_autopublish?.autopublish_mode}
            />
            <SummaryField
              label="Archive autopublish enabled"
              value={summary.workflows.archive_autopublish?.autopublish_enabled}
            />
            <SummaryField
              label="Controlled apply actionable"
              value={summary.workflows.archive_autopublish?.controlled_apply_actionable}
            />
          </dl>
          <p className="text-xs text-secondary-500">{LSC_ADMIN_LEAGUE_SUMMARY_ARCHIVE_NOTE}</p>
          <Link
            to={lscPath(`/leagues/${identity.league_id}/needs-attention`)}
            className="inline-flex text-primary-300 hover:text-primary-200"
            data-testid="lsc-admin-summary-needs-attention-link"
          >
            Open Needs Attention
          </Link>
        </SummaryCard>

        <SummaryCard title="Support issues" testId="lsc-admin-summary-issues-card">
          <dl className="grid gap-2 mb-3">
            <SummaryField label="Open issues" value={summary.issues.open_issue_count} />
            <SummaryField label="Launch blockers" value={summary.issues.launch_blocker_issue_count} />
            <SummaryField label="Total issues" value={summary.issues.total_issue_count} />
          </dl>
          {latestIssues.length === 0 ? (
            <p className="text-secondary-500">{LSC_ADMIN_LEAGUE_SUMMARY_ISSUES_EMPTY}</p>
          ) : (
            <ul className="space-y-2">
              {latestIssues.map((issue) => (
                <li key={issue.issue_id} className="rounded border border-secondary-800 p-2">
                  <Link
                    to={lscPath(`/admin/issues/${issue.issue_id}`)}
                    className="text-primary-300 hover:text-primary-200"
                    data-testid="lsc-admin-summary-issue-link"
                  >
                    {issue.issue_id}
                  </Link>
                  <p className="text-white mt-1">{issue.title}</p>
                  <p className="text-secondary-500 text-xs">
                    {supportIssueCategoryLabel((issue.category ?? 'other') as LscSupportIssueCategory)} ·{' '}
                    {supportIssueSeverityLabel((issue.severity ?? 'medium') as LscSupportIssueSeverity)} ·{' '}
                    {adminIssueStatusLabel(issue.status || 'open')}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </SummaryCard>
      </div>

      <p className="text-xs text-secondary-600" data-testid="lsc-admin-summary-no-mutation-note">
        Read-only admin observability. No mutation controls are available on this page.
      </p>
      <div className="hidden" data-testid="lsc-admin-summary-forbidden-controls">
        {ADMIN_FORBIDDEN_CONTROL_LABELS.join(', ')}
      </div>
    </div>
  );
}

const LscAdminLeagueSummaryPage: React.FC = () => {
  const { leagueId = '' } = useParams<{ leagueId: string }>();
  const [summary, setSummary] = useState<LscAdminLeagueSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!leagueId) {
      setError('League id is required.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    void getLscAdminLeagueSummary(leagueId)
      .then((payload) => setSummary(payload))
      .catch((err) => setError(parseAdminLeagueSummaryError(err)))
      .finally(() => setLoading(false));
  }, [leagueId]);

  return <LscAdminLeagueSummaryPageView summary={summary} loading={loading} error={error} />;
};

export default LscAdminLeagueSummaryPage;
